import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe } from "lucide-react";
import Logo from "@/components/Logo";

export default function Header() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { lang: langParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const lang = langParam === "en" ? "en" : "ar";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const base = `/${lang}`;
  const links = [
    { to: `${base}#how`, label: t("nav.how") },
    { to: `${base}#projects`, label: t("nav.projects") },
    { to: `${base}#values`, label: t("nav.about") },
    { to: `${base}#contact`, label: t("nav.contact") },
  ];

  const switchLang = (target) => {
    if (target === lang) return;
    const path = location.pathname;
    const newPath = path.replace(/^\/(ar|en)/, `/${target}`);
    i18n.changeLanguage(target);
    document.documentElement.lang = target === "en" ? "en" : "es-AR";
    navigate(newPath + location.hash, { replace: false });
  };

  return (
    <header
      className={`zv-header fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? "backdrop-blur bg-[#f6f0e2]/85 border-b border-[#e6ded0]" : "bg-transparent"
      }`}
    >
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 zv-btn zv-btn-primary">
        {t("nav.skip")}
      </a>
      <div className="zv-container flex items-center justify-between h-[72px]">
        <Link to={base} aria-label="Zantravora" className="flex items-center">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Principal">
          {links.map((l) => (
            <a key={l.to} href={l.to} className="text-[14px] text-[#0d1117] zv-underline">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <div className="flex items-center gap-1 border border-[#0d1117]/20 rounded-full p-1">
            <button
              onClick={() => switchLang("ar")}
              aria-pressed={lang === "ar"}
              className={`px-3 py-1 rounded-full text-[12px] transition ${
                lang === "ar" ? "bg-[#0d1117] text-[#f6f0e2]" : "text-[#0d1117]"
              }`}
            >
              ES
            </button>
            <button
              onClick={() => switchLang("en")}
              aria-pressed={lang === "en"}
              className={`px-3 py-1 rounded-full text-[12px] transition ${
                lang === "en" ? "bg-[#0d1117] text-[#f6f0e2]" : "text-[#0d1117]"
              }`}
            >
              EN
            </button>
          </div>
          <a href={`${base}#contact`} className="zv-btn zv-btn-primary">
            {t("nav.contact")}
          </a>
        </div>

        <button
          onClick={() => setOpen(true)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#0d1117]/20"
          aria-label={t("nav.open_menu")}
        >
          <Menu size={20} />
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 bg-[#0d1117] text-[#f6f0e2] lg:hidden flex flex-col">
          <div className="zv-container flex items-center justify-between h-[72px]">
            <Logo variant="light" />
            <button
              onClick={() => setOpen(false)}
              aria-label={t("nav.close_menu")}
              className="w-10 h-10 rounded-full border border-white/20 inline-flex items-center justify-center"
            >
              <X size={20} />
            </button>
          </div>
          <nav className="zv-container flex-1 flex flex-col gap-6 pt-10" aria-label="Móvil">
            {links.map((l) => (
              <a
                key={l.to}
                href={l.to}
                onClick={() => setOpen(false)}
                className="zv-serif text-[36px] leading-tight"
              >
                {l.label}
              </a>
            ))}
            <div className="mt-6 flex items-center gap-2">
              <Globe size={16} />
              <button
                onClick={() => switchLang("ar")}
                className={`px-3 py-1 rounded-full text-[13px] border ${
                  lang === "ar" ? "bg-[#f6f0e2] text-[#0d1117] border-transparent" : "border-white/30"
                }`}
              >
                Español
              </button>
              <button
                onClick={() => switchLang("en")}
                className={`px-3 py-1 rounded-full text-[13px] border ${
                  lang === "en" ? "bg-[#f6f0e2] text-[#0d1117] border-transparent" : "border-white/30"
                }`}
              >
                English
              </button>
            </div>
            <a
              href={`${base}#contact`}
              onClick={() => setOpen(false)}
              className="zv-btn zv-btn-cream mt-8 self-start"
            >
              {t("nav.contact")}
            </a>
          </nav>
          <div className="zv-container py-8 text-[12px] text-white/60">
            Zantravora · Buenos Aires
          </div>
        </div>
      )}
    </header>
  );
}
